import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  FileText, 
  Brain, 
  ClipboardList, 
  Sparkles,
  ArrowRight,
  Check,
  BookOpen,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Zap,
  Target,
  TrendingUp,
  Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

// Animated floating math symbols
const FloatingSymbol = ({ children, delay, duration, x, y }) => (
  <motion.div
    className="absolute text-primary/10 font-mono text-4xl md:text-6xl font-bold select-none pointer-events-none"
    style={{ left: x, top: y }}
    animate={{
      y: [0, -20, 0],
      rotate: [0, 5, -5, 0],
      opacity: [0.1, 0.2, 0.1]
    }}
    transition={{
      duration: duration || 4,
      delay: delay || 0,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    {children}
  </motion.div>
);

// Animated counter component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration]);
  
  return <span>{count}{suffix}</span>;
};

// Processing animation component
const ProcessingAnimation = () => {
  const steps = ['Extracting text...', 'Analyzing concepts...', 'Generating questions...', 'Creating flashcards...'];
  const [currentStep, setCurrentStep] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % steps.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [steps]);
  
  return (
    <div className="flex items-center gap-3">
      <motion.div
        className="w-3 h-3 bg-primary rounded-full"
        animate={{ scale: [1, 1.5, 1] }}
        transition={{ duration: 0.6, repeat: Infinity }}
      />
      <AnimatePresence mode="wait">
        <motion.span
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="text-sm font-mono text-primary"
        >
          {steps[currentStep]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showQuizAnswer, setShowQuizAnswer] = useState(false);
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [demoStep, setDemoStep] = useState(0);

  // Auto-play demo animation
  useEffect(() => {
    if (showDemo) {
      const interval = setInterval(() => {
        setDemoStep(prev => (prev < 4 ? prev + 1 : prev));
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [showDemo]);

  const features = [
    {
      icon: Brain,
      title: 'EXPLAIN CONCEPTS',
      description: 'Get step-by-step explanations of any concept from your uploaded material',
      color: 'bg-blue-500'
    },
    {
      icon: ClipboardList,
      title: 'PRACTICE QUIZZES',
      description: '10-15 targeted questions with detailed solutions',
      color: 'bg-green-500'
    },
    {
      icon: FileText,
      title: 'PRACTICE EXAMS',
      description: 'Full 20-question exams to simulate test conditions',
      color: 'bg-purple-500'
    },
    {
      icon: Sparkles,
      title: 'FLASHCARDS',
      description: 'Key formulas and definitions for quick review',
      color: 'bg-orange-500'
    }
  ];

  const steps = [
    { num: '01', title: 'Upload Your Slides', desc: 'Drop any PDF — lecture slides, homework, or notes', icon: Upload },
    { num: '02', title: 'Pick Your Study Tool', desc: 'Quiz, practice exam, flashcards, or explanations', icon: Target },
    { num: '03', title: 'Study Instantly', desc: 'Get AI-generated content ready in seconds', icon: TrendingUp }
  ];

  const stats = [
    { value: 10000, suffix: '+', label: 'Questions Generated' },
    { value: 95, suffix: '%', label: 'Student Satisfaction' },
    { value: 2, suffix: 'min', label: 'Avg. Generation Time' }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Floating Math Symbols Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <FloatingSymbol x="5%" y="15%" delay={0}>∫</FloatingSymbol>
        <FloatingSymbol x="90%" y="20%" delay={0.5} duration={5}>∂</FloatingSymbol>
        <FloatingSymbol x="15%" y="60%" delay={1}>Σ</FloatingSymbol>
        <FloatingSymbol x="85%" y="70%" delay={1.5} duration={4.5}>π</FloatingSymbol>
        <FloatingSymbol x="50%" y="10%" delay={2}>∞</FloatingSymbol>
        <FloatingSymbol x="70%" y="85%" delay={0.8}>Δ</FloatingSymbol>
        <FloatingSymbol x="25%" y="80%" delay={1.2} duration={5.5}>∇</FloatingSymbol>
      </div>

      {/* Navigation */}
      <nav className="border-b-2 border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <motion.div 
              className="w-10 h-10 bg-primary flex items-center justify-center"
              whileHover={{ scale: 1.05, rotate: 5 }}
            >
              <BookOpen className="w-6 h-6 text-white" />
            </motion.div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
          <div className="flex gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button data-testid="dashboard-btn" className="h-12 px-6 font-bold uppercase tracking-wide">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button data-testid="login-btn" variant="outline" className="h-12 px-6 font-medium border-2">
                    Log In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button data-testid="signup-btn" className="h-12 px-6 font-bold uppercase tracking-wide">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section - More Dynamic */}
      <section className="py-16 md:py-24 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <motion.div 
              className="md:col-span-6"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.span 
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-primary mb-4 bg-primary/10 px-3 py-1"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Zap className="w-3 h-3" />
                AI-POWERED STUDY TOOLS
              </motion.span>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mb-6">
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="block"
                >
                  TURN YOUR SLIDES
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="block"
                >
                  INTO{' '}
                  <span className="text-primary relative">
                    PRACTICE EXAMS
                  </span>
                </motion.span>
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="block text-muted-foreground text-2xl sm:text-3xl mt-2"
                >
                  IN SECONDS
                </motion.span>
              </h1>
              <motion.p 
                className="text-lg text-muted-foreground mb-8 max-w-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                Upload your lecture slides, homework, or notes as a PDF. Get AI-generated explanations, quizzes, flashcards, and practice exams tailored to your course.
              </motion.p>
              <motion.div 
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                  <Button 
                    data-testid="get-started-btn"
                    className="h-14 px-8 text-lg font-bold uppercase tracking-wide w-full sm:w-auto group"
                  >
                    Start Studying Free
                    <motion.span
                      className="ml-2"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <ArrowRight className="w-5 h-5" />
                    </motion.span>
                  </Button>
                </Link>
                <Button 
                  variant="outline"
                  className="h-14 px-8 text-lg font-medium border-2 w-full sm:w-auto"
                  onClick={() => setShowDemo(true)}
                  data-testid="watch-demo-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  See How It Works
                </Button>
              </motion.div>
              <motion.div 
                className="mt-8 flex flex-wrap items-center gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                {[
                  '5 free generations daily',
                  'No credit card needed',
                  'Works instantly'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1 + i * 0.1 }}
                    >
                      <Check className="w-5 h-5 text-primary" />
                    </motion.div>
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Interactive Demo Card */}
            <motion.div 
              className="md:col-span-6"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="relative">
                {/* Animated glow effect */}
                <motion.div
                  className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 blur-2xl"
                  animate={{ opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                
                <div className="relative bg-white border-2 border-border shadow-2xl">
                  {/* Demo header */}
                  <div className="border-b-2 border-border p-4 flex items-center justify-between bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">calc_ai_demo.pdf</span>
                  </div>

                  {/* Demo content with animations */}
                  <div className="p-6">
                    <AnimatePresence mode="wait">
                      {!showDemo ? (
                        <motion.div
                          key="idle"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="space-y-4"
                        >
                          {/* Upload area */}
                          <motion.div 
                            className="border-2 border-dashed border-primary/30 p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                            whileHover={{ scale: 1.02 }}
                            onClick={() => setShowDemo(true)}
                          >
                            <motion.div
                              animate={{ y: [0, -10, 0] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              <Upload className="w-12 h-12 text-primary mx-auto mb-3" />
                            </motion.div>
                            <p className="font-medium">Drop your PDF here</p>
                            <p className="text-sm text-muted-foreground">or click to see demo</p>
                          </motion.div>

                          {/* Quick stats */}
                          <div className="grid grid-cols-3 gap-4 pt-4">
                            {stats.map((stat, i) => (
                              <motion.div
                                key={i}
                                className="text-center"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 + i * 0.1 }}
                              >
                                <p className="font-heading font-black text-2xl text-primary">
                                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                                </p>
                                <p className="text-xs text-muted-foreground">{stat.label}</p>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="demo"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="space-y-4"
                        >
                          {/* Processing animation */}
                          {demoStep < 4 && (
                            <div className="bg-secondary p-4 rounded">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-mono text-sm">derivatives_ch3.pdf</span>
                                <span className="text-xs text-primary font-medium">
                                  {demoStep < 3 ? 'Processing...' : 'Complete!'}
                                </span>
                              </div>
                              <div className="h-2 bg-border overflow-hidden">
                                <motion.div
                                  className="h-full bg-primary"
                                  initial={{ width: '0%' }}
                                  animate={{ width: `${(demoStep + 1) * 25}%` }}
                                  transition={{ duration: 0.5 }}
                                />
                              </div>
                              <div className="mt-3">
                                <ProcessingAnimation />
                              </div>
                            </div>
                          )}

                          {/* Generated content preview */}
                          {demoStep >= 2 && (
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="space-y-3"
                            >
                              {[
                                { icon: Brain, label: '5 concepts explained', color: 'bg-blue-500' },
                                { icon: ClipboardList, label: '12 quiz questions', color: 'bg-green-500' },
                                { icon: Sparkles, label: '8 flashcards', color: 'bg-orange-500' }
                              ].slice(0, demoStep - 1).map((item, i) => (
                                <motion.div
                                  key={i}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.2 }}
                                  className="flex items-center gap-3 p-3 bg-white border-2 border-border"
                                >
                                  <div className={`w-8 h-8 ${item.color} flex items-center justify-center`}>
                                    <item.icon className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-medium text-sm">{item.label}</span>
                                  <Check className="w-4 h-4 text-green-500 ml-auto" />
                                </motion.div>
                              ))}
                            </motion.div>
                          )}

                          {demoStep >= 4 && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="text-center pt-4"
                            >
                              <p className="text-sm text-muted-foreground mb-3">Ready to try it yourself?</p>
                              <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
                                <Button className="font-bold uppercase">
                                  Start Now <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </Link>
                            </motion.div>
                          )}

                          {/* Reset button */}
                          <button
                            onClick={() => { setShowDemo(false); setDemoStep(0); }}
                            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mx-auto"
                          >
                            <RotateCcw className="w-3 h-3" /> Reset demo
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works - Animated */}
      <section className="py-16 bg-secondary border-y-2 border-border relative">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              HOW IT WORKS
            </span>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-12">
              THREE SIMPLE STEPS
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="bg-white border-2 border-border p-6 relative group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <motion.span 
                  className="font-heading font-black text-6xl text-primary/10 absolute top-4 right-4"
                  whileHover={{ scale: 1.1, color: 'rgba(59, 130, 246, 0.3)' }}
                >
                  {step.num}
                </motion.span>
                <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.desc}</p>
                
                {/* Connecting line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-border" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-2"
            >
              <div className="w-12 h-12 bg-primary/10 mx-auto flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold">Built for Students</h3>
              <p className="text-sm text-muted-foreground">Designed specifically for calculus exam prep</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="space-y-2"
            >
              <div className="w-12 h-12 bg-primary/10 mx-auto flex items-center justify-center">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold">Study in Seconds</h3>
              <p className="text-sm text-muted-foreground">Turn class slides into study materials instantly</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-2"
            >
              <div className="w-12 h-12 bg-primary/10 mx-auto flex items-center justify-center">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold">Ace Your Exams</h3>
              <p className="text-sm text-muted-foreground">Designed for fast, focused exam preparation</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Example Preview Section - Enhanced */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              EXAMPLE OUTPUT
            </span>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-4">
              SEE WHAT YOU'LL GET
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Here's a preview of the AI-generated study materials. Upload your own PDFs to get personalized content.
            </p>
          </motion.div>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Example Quiz Question */}
            <motion.div
              className="border-2 border-border hover:border-green-500 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="bg-green-500 text-white p-3 flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                <span className="font-heading font-bold text-sm">PRACTICE QUIZ</span>
              </div>
              <div className="p-4">
                <div className="flex items-start gap-3 mb-4">
                  <motion.span 
                    className="w-8 h-8 bg-primary text-white flex items-center justify-center font-bold flex-shrink-0 text-sm"
                    whileHover={{ scale: 1.1 }}
                  >
                    3
                  </motion.span>
                  <div>
                    <p className="font-medium text-sm">
                      Find the derivative of f(x) = x³ · sin(x)
                    </p>
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <p>A) 3x² · sin(x)</p>
                      <p className="text-green-600 font-medium">B) 3x² · sin(x) + x³ · cos(x)</p>
                      <p>C) x³ · cos(x)</p>
                      <p>D) 3x² · cos(x)</p>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => setShowQuizAnswer(!showQuizAnswer)}
                  className="w-full border-t-2 border-border pt-3 flex items-center justify-center gap-2 text-sm font-medium text-primary hover:underline"
                  data-testid="show-quiz-answer-btn"
                >
                  {showQuizAnswer ? (
                    <>Hide Answer <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Show Answer <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
                <AnimatePresence>
                  {showQuizAnswer && (
                    <motion.div 
                      className="mt-3 p-3 bg-green-50 text-sm border-l-4 border-green-500"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <p className="font-medium text-green-600 mb-2">Answer: B</p>
                      <p className="text-muted-foreground">
                        Using the Product Rule: (uv)' = u'v + uv'<br/>
                        u = x³, u' = 3x²<br/>
                        v = sin(x), v' = cos(x)<br/>
                        f'(x) = 3x² · sin(x) + x³ · cos(x)
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Example Flashcard */}
            <motion.div
              className="border-2 border-border hover:border-orange-500 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="bg-orange-500 text-white p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-heading font-bold text-sm">FLASHCARD</span>
                </div>
                <button 
                  onClick={() => setFlashcardFlipped(false)}
                  className="hover:bg-white/20 p-1 rounded"
                  data-testid="reset-flashcard-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
              <div 
                className="p-6 h-48 flex items-center justify-center cursor-pointer relative"
                onClick={() => setFlashcardFlipped(!flashcardFlipped)}
                style={{ perspective: '1000px' }}
                data-testid="example-flashcard"
              >
                <motion.div
                  className="w-full h-full absolute inset-0 p-6 flex flex-col items-center justify-center text-center"
                  animate={{ rotateY: flashcardFlipped ? 180 : 0 }}
                  transition={{ duration: 0.6, type: "spring" }}
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  <span className="font-mono text-xs uppercase tracking-wider text-orange-500 mb-2">
                    DERIVATIVES
                  </span>
                  <p className="font-heading font-bold text-xl">
                    Chain Rule
                  </p>
                  <motion.p 
                    className="text-sm text-muted-foreground mt-3"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Tap to flip
                  </motion.p>
                </motion.div>
                <motion.div
                  className="w-full h-full absolute inset-0 p-6 flex flex-col items-center justify-center text-center bg-orange-50"
                  animate={{ rotateY: flashcardFlipped ? 0 : -180 }}
                  transition={{ duration: 0.6, type: "spring" }}
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  <p className="text-sm">
                    If y = f(g(x)), then<br/>
                    <span className="font-mono font-bold text-orange-600 text-lg">dy/dx = f'(g(x)) · g'(x)</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-3 italic">
                    "Derivative of outside × derivative of inside"
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Example Explanation */}
            <motion.div
              className="border-2 border-border border-l-4 border-l-blue-500 hover:border-blue-500 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <div className="bg-blue-500 text-white p-3 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                <span className="font-heading font-bold text-sm">STEP-BY-STEP</span>
              </div>
              <div className="p-4">
                <h4 className="font-heading font-bold mb-3">Finding d/dx[e^(2x)]</h4>
                <div className="space-y-3 text-sm">
                  {[
                    'Identify the outer function: e^u where u = 2x',
                    'Apply chain rule: d/dx[e^u] = e^u · du/dx',
                    'Find du/dx: d/dx[2x] = 2',
                    null // Final step is special
                  ].map((step, i) => step && (
                    <motion.div 
                      key={i} 
                      className="flex gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <span className="w-6 h-6 bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                      <p className="text-muted-foreground">{step}</p>
                    </motion.div>
                  ))}
                  <motion.div 
                    className="flex gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 }}
                    viewport={{ once: true }}
                  >
                    <span className="w-6 h-6 bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                    <div>
                      <p className="text-muted-foreground">Combine:</p>
                      <p className="font-mono font-bold text-blue-600 mt-1">d/dx[e^(2x)] = 2e^(2x)</p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA after examples */}
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-muted-foreground mb-4">
              Ready to generate study materials from your own notes?
            </p>
            <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
              <Button 
                data-testid="example-cta-btn"
                className="h-12 px-8 font-bold uppercase tracking-wide group"
              >
                Try It Free
                <motion.span
                  className="ml-2 inline-block"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6 bg-secondary border-y-2 border-border">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              STUDY TOOLS
            </span>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-12">
              EVERYTHING YOU NEED TO ACE YOUR COUSE
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className={`border-2 p-6 bg-white transition-all cursor-pointer ${
                  hoveredFeature === i ? 'border-primary shadow-lg' : 'border-border'
                }`}
                onMouseEnter={() => setHoveredFeature(i)}
                onMouseLeave={() => setHoveredFeature(null)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <motion.div
                  className={`w-12 h-12 ${feature.color} flex items-center justify-center mb-4`}
                  whileHover={{ rotate: 5 }}
                >
                  <feature.icon className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="font-heading font-bold text-sm mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                PRICING
              </span>
              <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-4">
                START FREE, UPGRADE WHEN READY
              </h2>
              <p className="text-muted-foreground mb-8">
                5 free generations per day. Upgrade for unlimited access at just $7.99/month.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-4">
              <motion.div 
                className="bg-white border-2 border-border p-6 text-left"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <h3 className="font-heading font-bold mb-2">FREE</h3>
                <p className="text-3xl font-heading font-black mb-4">$0</p>
                <ul className="space-y-2 text-sm">
                  {['5 generations per day', 'Resets daily', 'All study tool types'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
              <motion.div 
                className="bg-primary text-white border-2 border-primary p-6 text-left relative overflow-hidden"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <motion.div
                  className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <h3 className="font-heading font-bold mb-2">PRO</h3>
                <p className="text-3xl font-heading font-black mb-4">$7.99<span className="text-lg font-normal">/mo</span></p>
                <ul className="space-y-2 text-sm">
                  {['Unlimited generations', 'Priority AI processing', 'Save all study sessions'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6 bg-primary text-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-2xl md:text-4xl tracking-tight mb-4">
              READY TO ACE YOUR NEXT EXAM?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Join thousands of students who are studying smarter, not harder.
            </p>
            <Link to={isAuthenticated ? "/dashboard" : "/signup"}>
              <Button 
                data-testid="final-cta-btn"
                variant="secondary"
                className="h-14 px-8 text-lg font-bold uppercase tracking-wide text-primary"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-border py-8 px-6 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-sm">CALC AI</span>
          </div>
          <p className="text-sm text-muted-foreground">
          © Powered by FHYAS LLC.
          </p>
        </div>
      </footer>
    </div>
  );
}
